import { supabase } from '@/lib/supabaseClient';

// Tạo các hàm độc lập để tránh circular dependency

/**
 * Lấy thứ hạng global leaderboard của người dùng
 * @param {string} userId ID của người dùng
 * @returns {Promise<{rank: number, totalUsers: number}>} Object chứa rank và totalUsers
 */
export async function getUserGlobalRank(userId) {
  try {
    // Tìm global leaderboard
    const { data: leaderboard } = await supabase
      .from('leaderboards')
      .select('id')
      .eq('type', 'global')
      .single();
    
    if (!leaderboard) return { rank: 0, totalUsers: 0 };
    
    // Lấy rank hiện tại
    const { data: entry } = await supabase
      .from('leaderboard_entries')
      .select('rank')
      .eq('leaderboard_id', leaderboard.id)
      .eq('user_id', userId)
      .single();
    
    // Đếm tổng số người dùng
    const { count } = await supabase
      .from('leaderboard_entries')
      .select('*', { count: 'exact', head: true })
      .eq('leaderboard_id', leaderboard.id);
    
    return {
      rank: entry?.rank || 0,
      totalUsers: count || 0
    };
  } catch (error) {
    console.error('Error getting global rank:', error);
    return { rank: 0, totalUsers: 0 };
  }
}

/**
 * Lấy thứ hạng course leaderboard của người dùng
 * @param {string} userId ID của người dùng
 * @returns {Promise<number>} Thứ hạng
 */
export async function getUserCourseRank(userId) {
  try {
    // Tìm course leaderboard
    const { data: leaderboard } = await supabase
      .from('leaderboards')
      .select('id')
      .eq('type', 'course')
      .single();
    
    if (!leaderboard) return 0;
    
    // Lấy rank hiện tại
    const { data: entry } = await supabase
      .from('leaderboard_entries')
      .select('rank')
      .eq('leaderboard_id', leaderboard.id)
      .eq('user_id', userId)
      .single();
    
    return entry?.rank || 0;
  } catch (error) {
    console.error('Error getting course rank:', error);
    return 0;
  }
}

/**
 * Cập nhật điểm global leaderboard
 * @param {string} userId ID người dùng
 * @param {number} score Điểm số
 * @param {Object} metadata Metadata 
 * @returns {Promise<any>} Kết quả cập nhật
 */
export async function updateUserGlobalScore(userId, score, metadata = { ai_credits: 0 }) {
  try {
    // Tìm global leaderboard
    const { data: leaderboard } = await supabase
      .from('leaderboards')
      .select('id')
      .eq('type', 'global')
      .single();
    
    if (!leaderboard) throw new Error('Không tìm thấy global leaderboard');
    
    // Cập nhật hoặc thêm mới
    const { data, error } = await supabase
      .from('leaderboard_entries')
      .upsert({
        leaderboard_id: leaderboard.id,
        user_id: userId,
        score,
        metadata,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'leaderboard_id,user_id'
      });
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating global score:', error);
    throw error;
  }
}

/**
 * Cập nhật điểm course leaderboard
 * @param {string} userId ID người dùng
 * @param {number} score Điểm số
 * @param {Object} metadata Metadata 
 * @returns {Promise<any>} Kết quả cập nhật
 */
export async function updateUserCourseScore(userId, score, metadata = { completed_courses: 0 }) {
  try {
    // Tìm course leaderboard
    const { data: leaderboard } = await supabase
      .from('leaderboards')
      .select('id')
      .eq('type', 'course')
      .single();
    
    if (!leaderboard) throw new Error('Không tìm thấy course leaderboard');
    
    // Cập nhật hoặc thêm mới
    const { data, error } = await supabase
      .from('leaderboard_entries')
      .upsert({
        leaderboard_id: leaderboard.id,
        user_id: userId,
        score,
        metadata,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'leaderboard_id,user_id'
      });
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating course score:', error);
    throw error;
  }
}

/**
 * Lấy top users trong global leaderboard
 * @param {number} limit Số lượng tối đa
 * @returns {Promise<Array>} Danh sách users
 */
export async function getTopGlobalUsers(limit = 10) {
  try {
    // Tìm global leaderboard
    const { data: leaderboard } = await supabase
      .from('leaderboards')
      .select('id')
      .eq('type', 'global')
      .single();
    
    if (!leaderboard) return [];
    
    // Lấy danh sách users theo rank
    const { data, error } = await supabase
      .from('leaderboard_entries')
      .select(`
        *,
        users:user_id (
          id,
          username,
          full_name,
          avatar_url
        )
      `)
      .eq('leaderboard_id', leaderboard.id)
      .order('rank', { ascending: true })
      .limit(limit);
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting top global users:', error);
    return [];
  }
}

/**
 * Lấy top users trong course leaderboard
 * @param {number} limit Số lượng tối đa
 * @returns {Promise<Array>} Danh sách users
 */
export async function getTopCourseUsers(limit = 10) {
  try {
    // Tìm course leaderboard
    const { data: leaderboard } = await supabase
      .from('leaderboards')
      .select('id')
      .eq('type', 'course')
      .single();
    
    if (!leaderboard) return [];
    
    // Lấy danh sách users theo rank
    const { data, error } = await supabase
      .from('leaderboard_entries')
      .select(`
        *,
        users:user_id (
          id,
          username,
          full_name,
          avatar_url
        )
      `)
      .eq('leaderboard_id', leaderboard.id)
      .order('rank', { ascending: true })
      .limit(limit);
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting top course users:', error);
    return [];
  }
}

/**
 * Xóa cache leaderboard
 */
export function clearLeaderboardCache() {
  // Xóa các cache có tiền tố liên quan đến leaderboard
  try {
    localStorage.removeItem('cache_user_global_rank_');
    localStorage.removeItem('cache_user_course_rank_');
    localStorage.removeItem('cache_top_global_users_');
    localStorage.removeItem('cache_top_course_users_');
  } catch (e) {
    console.warn('Không thể xóa cache leaderboard:', e);
  }
}

// Khuyến nghị: Hiển thị warning trong console khi sử dụng file cũ
console.warn(
  "DEPRECATED: Bạn đang sử dụng LeaderboardService.js thay vì leaderboardService.ts. " +
  "Vui lòng cập nhật import của bạn để sử dụng phiên bản mới."
);

// Export default
export default {
  getUserGlobalRank,
  getUserCourseRank,
  updateUserGlobalScore,
  updateUserCourseScore,
  getTopGlobalUsers,
  getTopCourseUsers,
  clearLeaderboardCache
};
